
import { UserIcon, Phone, Bell, MapPin, Info } from "lucide-react";
import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { useMapUrl } from "@/hooks/useMapUrl";

interface ProfileType {
  id: string;
  display_name: string | null;
  email: string | null;
  updated_at: string | null;
  address: string | null;
  location: string | null;
}

interface ProfileViewModeProps {
  profileData: ProfileType | null;
}

export const ProfileViewMode = ({
  profileData
}: ProfileViewModeProps) => {
  const { preferences } = useNotificationPreferences();
  const { mapUrl, mapEmbedUrl, isLoadingKey, mapsApiKey } = useMapUrl(profileData?.location || null);

  // Debug logging for map issues
  console.log('[ProfileViewMode] Map debug:', { 
    location: profileData?.location, 
    mapEmbedUrl, 
    isLoadingKey, 
    hasApiKey: !!mapsApiKey 
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-1">
          <h3 className="text-sm flex items-center gap-2 font-medium text-muted-foreground">
            <UserIcon className="h-4 w-4" />
            Display Name
          </h3>
          <p className="text-base font-medium">{profileData?.display_name || 'Not provided'}</p>
        </div>
        
        <div className="md:col-span-2 space-y-3 border rounded-md p-5 bg-blue-50/50">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="h-5 w-5 text-blue-600" />
            <h3 className="text-base font-semibold text-blue-700">Contact Information</h3>
          </div>
          
          <div className="flex items-center gap-2 mb-3 text-sm bg-blue-100 p-3 rounded-md text-blue-800">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <p>We only share your contact information with vendors after you've booked their services.</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <h3 className="text-sm flex items-center gap-2 font-medium text-muted-foreground">
                <Phone className="h-4 w-4" />
                Phone Number
              </h3>
              <p className="text-base">{preferences?.notification_phone || 'Not provided'}</p>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-muted-foreground">Address</h3>
              <p className="text-base">{profileData?.address || 'Not provided'}</p>
            </div>
            
            <div className="space-y-1 md:col-span-2">
              <h3 className="text-sm font-medium text-muted-foreground">Map Location</h3>
              <p className="text-base break-words">{profileData?.location || 'Not provided'}</p>
            </div>
          </div>
          
          {/* Booking Notifications Section */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              <h4 className="text-sm font-semibold text-blue-700">Booking Notifications</h4>
            </div>
            
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-lg border p-3 shadow-sm bg-white">
                <div className="space-y-0.5">
                  <h5 className="text-sm font-medium">Email Notifications</h5>
                  <p className="text-xs text-muted-foreground">
                    Receive booking updates via email
                  </p>
                  <p className={`text-sm font-medium ${preferences?.email_booking_updates ? 'text-green-600' : 'text-gray-500'}`}>
                    {preferences?.email_booking_updates ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border p-3 shadow-sm bg-white">
                <div className="space-y-0.5">
                  <h5 className="text-sm font-medium">SMS Notifications</h5>
                  <p className="text-xs text-muted-foreground">
                    Receive booking updates via SMS
                  </p>
                  <p className={`text-sm font-medium ${preferences?.sms_booking_updates ? 'text-green-600' : 'text-gray-500'}`}>
                    {preferences?.sms_booking_updates ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              At least one notification method is enabled for booking updates.
            </p>
          </div>
          
          {(mapEmbedUrl || profileData?.location) && (
            <div className="mt-4 w-full">
              <div className="border rounded-md overflow-hidden w-full h-[250px]">
                {isLoadingKey ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    Loading map...
                  </div>
                ) : mapEmbedUrl && mapsApiKey ? (
                  <iframe 
                    src={mapEmbedUrl}
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen={false}
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Location Map"
                  />
                ) : profileData?.location ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                    <p className="text-sm text-center">Map preview not available</p>
                    <p className="text-xs text-center mt-1">Location: {profileData.location}</p>
                    {!mapsApiKey && <p className="text-xs text-center mt-1">API key not loaded</p>}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No location provided
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
